import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { FontFamily } from '@/api/types/font-family'
import { useFonts } from '@/providers/font'

interface FontFamilyPickerOptions {
  allFamiliesSorted: FontFamily[]
  fontFamilyIdValue: string
  resolvedDefaultFontFamilyId: number | undefined
  applyFontFamilyById: (fontFamilyId: number) => boolean
  restoreActiveEditor: () => void
}

/** The Font Family popover: open state, lazy face loading for the rows in view, and selection. */
export function useFontFamilyPicker({
  allFamiliesSorted,
  fontFamilyIdValue,
  resolvedDefaultFontFamilyId,
  applyFontFamilyById,
  restoreActiveEditor,
}: FontFamilyPickerOptions) {
  const { ensureFamilyFacesLoaded, loadFullCatalog } = useFonts()

  const fontSelectViewportRef = useRef<HTMLDivElement | null>(null)
  const fontSelectScrollRaf = useRef<number | null>(null)
  const [fontFamilySelectOpen, setFontFamilySelectOpen] = useState(false)
  const fontPickerOpenedByKeyboardRef = useRef(false)

  const ensureFontFamilyRowsInViewport = useCallback(() => {
    const root = fontSelectViewportRef.current

    if (!root) return
    const rootRect = root.getBoundingClientRect()
    const nodes = root.querySelectorAll('[data-lazy-font-family]')

    nodes.forEach((node) => {
      const el = node as HTMLElement
      const raw = el.dataset.lazyFontFamily
      const id = raw != null ? Number(raw) : Number.NaN

      if (!Number.isFinite(id)) return
      const rect = el.getBoundingClientRect()
      const visible = rect.bottom > rootRect.top && rect.top < rootRect.bottom

      if (visible) void ensureFamilyFacesLoaded(id)
    })
  }, [ensureFamilyFacesLoaded])

  const scheduleEnsureFontFamilyRowsInViewport = useCallback(() => {
    if (fontSelectScrollRaf.current != null) return
    fontSelectScrollRaf.current = requestAnimationFrame(() => {
      fontSelectScrollRaf.current = null
      ensureFontFamilyRowsInViewport()
    })
  }, [ensureFontFamilyRowsInViewport])

  useEffect(() => {
    if (!fontFamilySelectOpen) return
    if (!allFamiliesSorted.length) return

    const id = requestAnimationFrame(() => {
      ensureFontFamilyRowsInViewport()
    })

    return () => cancelAnimationFrame(id)
  }, [fontFamilySelectOpen, allFamiliesSorted.length, ensureFontFamilyRowsInViewport])

  const handleOpenChange = (open: boolean) => {
    setFontFamilySelectOpen(open)
    if (open) {
      loadFullCatalog()
      const cur = Number(fontFamilyIdValue)

      if (fontFamilyIdValue && Number.isFinite(cur)) {
        void ensureFamilyFacesLoaded(cur)
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => ensureFontFamilyRowsInViewport())
      })
    } else {
      // Restore the text editor caret whenever the popover closes, whether or not the
      // user selected a new family. Without this the cursor stays invisible after the
      // popover dismisses via Escape or an outside click.
      restoreActiveEditor()
    }
  }

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      fontPickerOpenedByKeyboardRef.current = true
    }
  }

  const handleOpenAutoFocus = (e: Event) => {
    if (!fontPickerOpenedByKeyboardRef.current) {
      e.preventDefault()
    }

    fontPickerOpenedByKeyboardRef.current = false
  }

  /** The pinned "Default Font" row. */
  const selectDefaultFamily = () => {
    if (!resolvedDefaultFontFamilyId) return
    if (!applyFontFamilyById(resolvedDefaultFontFamilyId)) return
    setFontFamilySelectOpen(false)
    restoreActiveEditor()
  }

  /** A row in the family list. */
  const selectFamily = (fontFamilyId: number, isFontFailed: boolean) => {
    if (isFontFailed) return
    if (!applyFontFamilyById(fontFamilyId)) return
    setFontFamilySelectOpen(false)
    restoreActiveEditor()
  }

  return {
    fontSelectViewportRef,
    fontFamilySelectOpen,
    setFontFamilySelectOpen,
    scheduleEnsureFontFamilyRowsInViewport,
    handleOpenChange,
    handleTriggerKeyDown,
    handleOpenAutoFocus,
    selectDefaultFamily,
    selectFamily,
  }
}
